import { useMemo, useRef } from "react";
import { useWatch, type UseFormReturn } from "react-hook-form";

import { isRichTextEmpty } from "@/lib/form-utils";

/**
 * Returns true when all language values for the given multilingual field are empty.
 * Uses a single useWatch to avoid sprinkling form.watch throughout components.
 *
 * @param name - The multilingual field name to watch
 * @param form - The react-hook-form instance
 * @param publishedFieldName - Optional: when provided, auto-resets this field to false when empty
 */
export const useMultilingualEmpty = (
  name: string,
  form: UseFormReturn<any>,
  publishedFieldName?: string,
): boolean => {
  const value = useWatch({ control: form.control, name });
  const prevEmptyRef = useRef<boolean | null>(null);

  const isEmpty = useMemo(() => {
    if (!value || typeof value !== "object") return true;

    const entries = Object.values(value as Record<string, unknown>);
    if (entries.length === 0) return true;

    return entries.every((entry) => isRichTextEmpty(entry));
  }, [value]);

  // Auto-reset published field to false when content becomes empty
  // Using ref to track previous state and avoid unnecessary setValue calls
  if (publishedFieldName && isEmpty && prevEmptyRef.current === false) {
    const currentPublished = form.getValues(publishedFieldName);
    if (currentPublished === true) {
      form.setValue(publishedFieldName, false, { shouldDirty: true });
    }
  }
  prevEmptyRef.current = isEmpty;

  return isEmpty;
};
